import React, { useState, useEffect, useRef } from 'react'
import favicon from "../assets/favicon.png"
import { Helmet } from 'react-helmet'
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { reSendMobileOTP, verifyMobileOTPAPI } from '../utils/api';
import { MdSecurity } from 'react-icons/md';
import { Spinner } from 'react-bootstrap';

const MobileOTP = () => {
    const [otp, setOtp] = useState(['', '', '', '']);
    const [loading, setLoading] = useState(false);
    const [resendTimer, setResendTimer] = useState(30);
    const [canResend, setCanResend] = useState(false);
    const [validated, setValidated] = useState(false);
    const inputRefs = useRef([]);
    const navigate = useNavigate();
    const mobile = localStorage.getItem("subagentmobile");

    useEffect(() => {
        if (!mobile) {
            toast.error("Session expired. Please login again.");
            navigate("/");
        }
        // Auto focus first box
        if (inputRefs.current[0]) inputRefs.current[0].focus();
    }, [navigate]);

    useEffect(() => {
        if (resendTimer > 0) {
            const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
            return () => clearTimeout(timer);
        } else {
            setCanResend(true);
        }
    }, [resendTimer]);

    const handleOtpChange = (index, value) => {
        if (!/^[0-9]?$/.test(value)) return;
        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);
        // Auto advance to next
        if (value && index < 3) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
        const newOtp = ['', '', '', ''];
        pasted.split('').forEach((char, i) => { newOtp[i] = char; });
        setOtp(newOtp);
        const nextFocus = Math.min(pasted.length, 3);
        inputRefs.current[nextFocus]?.focus();
    };

    const handleOTPSubmit = async (e) => {
        e.preventDefault();
        setValidated(true);
        const mobileotp = otp.join('');
        if (mobileotp.length !== 4) {
            return;
        }
        setLoading(true);
        const mobiledata = { mobile, mobileotp };
        verifyMobileOTPAPI("sub-agent-verify-mobile-otp", mobiledata)
            .then((res) => {
                if (res.data.status === 1) {
                    localStorage.setItem("subagentdata", res.data.data);
                    toast.success(res.data.msg);
                    navigate("/dashboard");
                } else {
                    toast.error(res.data.msg);
                }
            })
            .catch(() => {
                toast.error("Something went wrong. Please try again.");
            })
            .finally(() => {
                setLoading(false);
            });
    }

    const resendMobileOTP = () => {
        if (!canResend) return;
        if (mobile && mobile.length === 10) {
            reSendMobileOTP("sub-agent-resend-mobile-otp", mobile)
                .then((res) => {
                    if (res.data.status === 1) {
                        toast.success("Mobile OTP has been sent!");
                        setResendTimer(30);
                        setCanResend(false);
                        setOtp(['', '', '', '']);
                        setValidated(false);
                        inputRefs.current[0]?.focus();
                    } else {
                        toast.error("Failed to resend OTP. Try again.");
                    }
                })
                .catch(() => {
                    toast.error("Something went wrong. Please try again.");
                });
        } else {
            toast.error("Invalid Mobile Number!");
        }
    }

    const otpIncomplete = validated && otp.join('').length !== 4;

    return (
        <div className="login-wrapper">
            <Helmet>
                <title>SATKARTAR: MOBILE OTP</title>
                <link href="https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap" rel="stylesheet" />
            </Helmet>

            <style>{`
                .login-wrapper {
                    min-height: 100vh;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: radial-gradient(circle at 50% 0%, #f8fafc 0%, #e2e8f0 100%);
                    font-family: 'Nunito', sans-serif;
                    position: relative;
                    overflow: hidden;
                    padding: 20px;
                }
                .login-wrapper::before {
                    content: '';
                    position: absolute;
                    top: -20%; left: -10%;
                    width: 50vw; height: 50vw;
                    background: radial-gradient(circle, rgba(245,158,11,0.15) 0%, transparent 70%);
                    border-radius: 50%;
                    z-index: 1;
                    pointer-events: none;
                }
                .login-wrapper::after {
                    content: '';
                    position: absolute;
                    bottom: -20%; right: -10%;
                    width: 40vw; height: 40vw;
                    background: radial-gradient(circle, rgba(14,116,144,0.1) 0%, transparent 70%);
                    border-radius: 50%;
                    z-index: 1;
                    pointer-events: none;
                }
                .login-card {
                    background: rgba(255, 255, 255, 0.85);
                    backdrop-filter: blur(20px);
                    -webkit-backdrop-filter: blur(20px);
                    border: 1px solid rgba(255, 255, 255, 0.5);
                    border-radius: 24px;
                    padding: 48px 40px;
                    width: 100%;
                    max-width: 440px;
                    box-shadow: 0 20px 40px -10px rgba(0, 0, 0, 0.08);
                    z-index: 10;
                    position: relative;
                }
                .login-header { text-align: center; margin-bottom: 36px; }
                .login-logo {
                    width: 84px; height: 84px;
                    object-fit: contain;
                    margin-bottom: 20px;
                    border-radius: 20px;
                    background: #fff;
                    padding: 12px;
                    box-shadow: 0 8px 20px rgba(0,0,0,0.06);
                    border: 2px solid rgba(245, 158, 11, 0.3);
                }
                .login-title {
                    color: #0f172a;
                    font-size: 24px;
                    font-weight: 900;
                    letter-spacing: 1.5px;
                    margin: 0;
                }
                .login-subtitle {
                    color: #64748b;
                    font-size: 12px;
                    margin-top: 6px;
                    font-weight: 700;
                    letter-spacing: 0.5px;
                    text-transform: uppercase;
                }
                .otp-mobile-hint {
                    display: inline-block;
                    background: linear-gradient(135deg, rgba(245,158,11,0.1), rgba(234,88,12,0.06));
                    border: 1px solid rgba(245,158,11,0.2);
                    color: #92400e;
                    font-size: 12px;
                    font-weight: 700;
                    padding: 6px 14px;
                    border-radius: 999px;
                    margin-top: 10px;
                }
                .login-form-group {
                    margin-bottom: 28px;
                    position: relative;
                }
                .login-label {
                    display: block;
                    color: #475569;
                    font-size: 12px;
                    font-weight: 800;
                    margin-bottom: 16px;
                    text-transform: uppercase;
                    letter-spacing: 0.8px;
                    text-align: center;
                }

                /* ── 4-box OTP ── */
                .otp-boxes {
                    display: flex;
                    gap: 14px;
                    justify-content: center;
                }
                .otp-box {
                    width: 64px;
                    height: 68px;
                    text-align: center;
                    font-size: 28px;
                    font-weight: 900;
                    color: #0f172a;
                    background: #f1f5f9;
                    border: 2px solid #e2e8f0;
                    border-radius: 14px;
                    outline: none;
                    transition: all 0.25s ease;
                    font-family: 'Nunito', sans-serif;
                    box-shadow: inset 0 2px 4px rgba(0,0,0,0.02);
                    caret-color: #f59e0b;
                }
                .otp-box:focus {
                    border-color: #f59e0b;
                    background: #fff;
                    box-shadow: 0 0 0 4px rgba(245,158,11,0.15), inset 0 2px 4px rgba(0,0,0,0.02);
                }
                .otp-box.filled {
                    border-color: #f59e0b;
                    background: #fffbeb;
                    color: #92400e;
                }
                .otp-box.invalid {
                    border-color: #ef4444 !important;
                    background: #fff !important;
                    box-shadow: 0 0 0 4px rgba(239,68,68,0.1) !important;
                }
                .otp-error {
                    text-align: center;
                    color: #ef4444;
                    font-size: 11.5px;
                    font-weight: 700;
                    margin-top: 10px;
                }

                .resend-row {
                    display: flex;
                    justify-content: center;
                    margin-top: 14px;
                }
                .resend-btn {
                    background: none !important;
                    border: none !important;
                    font-size: 12.5px !important;
                    font-weight: 800 !important;
                    font-family: 'Nunito', sans-serif !important;
                    padding: 0 !important;
                    transition: color 0.2s !important;
                    cursor: pointer !important;
                }
                .resend-btn.active { color: #f59e0b !important; }
                .resend-btn.inactive { color: #94a3b8 !important; cursor: not-allowed !important; }

                .login-btn {
                    width: 100%;
                    background: linear-gradient(135deg, #f59e0b, #ea580c) !important;
                    color: #fff !important;
                    border: none !important;
                    border-radius: 12px !important;
                    padding: 12px !important;
                    font-size: 15px !important;
                    font-weight: 800 !important;
                    letter-spacing: 1px !important;
                    text-transform: uppercase !important;
                    transition: all 0.3s ease !important;
                    box-shadow: 0 8px 20px rgba(245, 158, 11, 0.25) !important;
                    display: flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    gap: 10px !important;
                    margin-top: 12px !important;
                    font-family: 'Nunito', sans-serif !important;
                    cursor: pointer !important;
                }
                .login-btn:hover {
                    transform: translateY(-2px) !important;
                    box-shadow: 0 12px 25px rgba(245, 158, 11, 0.35) !important;
                }
                .login-btn:disabled {
                    opacity: 0.7 !important;
                    transform: none !important;
                    cursor: not-allowed !important;
                }
                .back-link {
                    display: block;
                    text-align: center;
                    margin-top: 16px;
                    font-size: 12.5px;
                    font-weight: 700;
                    color: #64748b;
                    cursor: pointer;
                    text-decoration: none;
                }
                .back-link:hover { color: #f59e0b; }
                .footer-text {
                    text-align: center;
                    margin-top: 24px;
                    color: #64748b;
                    font-size: 11.5px;
                    font-weight: 700;
                    letter-spacing: 0.5px;
                }
            `}</style>

            <div className="login-card">
                <div className="login-header">
                    <img src={favicon} className="login-logo" alt="Satkartar Logo" />
                    <h4 className="login-title">VERIFY OTP</h4>
                    <div className="login-subtitle">Sub Agent Portal</div>
                    {mobile && (
                        <div className="otp-mobile-hint">
                            📱 OTP sent to +91 {mobile.replace(/(\d{6})(\d{4})/, '••••••$2')}
                        </div>
                    )}
                </div>

                <form noValidate onSubmit={handleOTPSubmit}>
                    <div className="login-form-group">
                        <label className="login-label">
                            <MdSecurity style={{ marginRight: 6, verticalAlign: 'middle' }} />
                            Enter 4-Digit OTP
                        </label>
                        <div className="otp-boxes" onPaste={handlePaste}>
                            {otp.map((digit, index) => (
                                <input
                                    key={index}
                                    ref={el => inputRefs.current[index] = el}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={1}
                                    value={digit}
                                    onChange={e => handleOtpChange(index, e.target.value)}
                                    onKeyDown={e => handleKeyDown(index, e)}
                                    className={`otp-box${digit ? ' filled' : ''}${otpIncomplete ? ' invalid' : ''}`}
                                />
                            ))}
                        </div>
                        {otpIncomplete && (
                            <div className="otp-error">⚠ Please enter the complete 4-digit OTP</div>
                        )}
                        <div className="resend-row">
                            <button
                                type="button"
                                className={`resend-btn ${canResend ? 'active' : 'inactive'}`}
                                onClick={resendMobileOTP}
                                disabled={!canResend}
                            >
                                {canResend ? '↩ Resend OTP' : `Resend OTP in ${resendTimer}s`}
                            </button>
                        </div>
                    </div>

                    {loading ? (
                        <button disabled type="submit" className="login-btn">
                            <Spinner animation="border" size="sm" /> VERIFYING...
                        </button>
                    ) : (
                        <button type="submit" className="login-btn">VERIFY & LOGIN</button>
                    )}
                </form>

                <span className="back-link" onClick={() => navigate('/')}>
                    ← Back to Login
                </span>

                <div className="footer-text">
                    &copy; {new Date().getFullYear()} Satkartar Life Limited.
                </div>
            </div>
        </div>
    )
}

export default MobileOTP